import { useState, useEffect } from 'react'
import { MicrophoneIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useOrientationStore } from '@/stores/orientation.store'
import { useAudioRecorder } from '@/hooks/useAudioRecorder'
import { voiceAPI } from '@/lib/voiceApi'
import type { OrientationRequest } from '@/lib/orientationApi'
import toast from 'react-hot-toast'

const MOTIFS = [
  'Eclampsie / Pre-eclampsie',
  'AVC (Accident Vasculaire Cerebral)',
  'Traumatisme / Accident',
  'Accouchement / Grossesse compliquee',
  'Douleur thoracique',
  'Detresse respiratoire',
  'Urgence pediatrique',
  'Hemorragie',
  'Brulure grave',
  'Autre',
]

const GRAVITES = [
  { value: 'LEGERE', label: 'Legere', color: 'bg-blue-100 text-blue-800' },
  { value: 'MODEREE', label: 'Moderee', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'GRAVE', label: 'Grave', color: 'bg-orange-100 text-orange-800' },
  { value: 'CRITIQUE', label: 'Critique', color: 'bg-red-100 text-red-800' },
] as const

const BESOINS = [
  'Obstetrique', 'Neurologie', 'Chirurgie', 'Cardiologie',
  'Reanimation', 'Pediatrie', 'Urgences', 'Maternite',
  'Sang O+', 'MgSO4', 'Oxygene', 'Ambulance SMUR',
]

// Map extracted motif keywords to the closest MOTIFS option
function matchMotif(extracted: string): string {
  const lower = extracted.toLowerCase()
  const mapping: Record<string, string> = {
    eclampsie: 'Eclampsie / Pre-eclampsie',
    'pre-eclampsie': 'Eclampsie / Pre-eclampsie',
    avc: 'AVC (Accident Vasculaire Cerebral)',
    vasculaire: 'AVC (Accident Vasculaire Cerebral)',
    traumatisme: 'Traumatisme / Accident',
    accident: 'Traumatisme / Accident',
    accouchement: 'Accouchement / Grossesse compliquee',
    grossesse: 'Accouchement / Grossesse compliquee',
    thoracique: 'Douleur thoracique',
    respiratoire: 'Detresse respiratoire',
    pediatrique: 'Urgence pediatrique',
    pediatrie: 'Urgence pediatrique',
    hemorragie: 'Hemorragie',
    brulure: 'Brulure grave',
  }
  for (const [keyword, motif] of Object.entries(mapping)) {
    if (lower.includes(keyword)) return motif
  }
  return 'Autre'
}

// Map extracted besoins to the closest BESOINS options
function matchBesoins(extracted: string[]): string[] {
  const matched: string[] = []
  for (const need of extracted) {
    const lower = need.toLowerCase()
    const found = BESOINS.find((b) => lower.includes(b.toLowerCase()) || b.toLowerCase().includes(lower))
    if (found && !matched.includes(found)) matched.push(found)
  }
  return matched
}

interface Props {
  onSubmit: (data: OrientationRequest) => void
}

export default function OrientationForm({ onSubmit }: Props) {
  const { isLoading } = useOrientationStore()
  const [motif, setMotif] = useState('')
  const [gravite, setGravite] = useState<string>('')
  const [besoins, setBesoins] = useState<string[]>([])
  const [contexte, setContexte] = useState('')
  const [latitude, setLatitude] = useState<number>(14.6937)
  const [longitude, setLongitude] = useState<number>(-17.4441)
  const [geoStatus, setGeoStatus] = useState<string>('')
  const [transcription, setTranscription] = useState<string>('')

  const recorder = useAudioRecorder()

  useEffect(() => {
    if (navigator.geolocation) {
      setGeoStatus('Localisation en cours...')
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude
          const lon = pos.coords.longitude
          if (lat >= 12.0 && lat <= 17.0 && lon >= -17.6 && lon <= -11.4) {
            setLatitude(lat)
            setLongitude(lon)
            setGeoStatus(`Position: ${lat.toFixed(4)}, ${lon.toFixed(4)}`)
          } else {
            setGeoStatus('Position par defaut (Dakar)')
          }
        },
        () => setGeoStatus('Position par defaut (Dakar)')
      )
    }
  }, [])

  const toggleBesoin = (b: string) => {
    setBesoins((prev) => prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b])
  }

  const handleVoiceToggle = async () => {
    if (recorder.state === 'recording') {
      // Stop recording and send to backend
      const blob = await recorder.stop()
      if (!blob) return

      recorder.setState('processing')
      try {
        const result = await voiceAPI.transcribe(blob)
        setTranscription(result.transcription)

        if (result.extracted) {
          const { motif: exMotif, gravite: exGravite, besoins: exBesoins, contexte: exContexte } = result.extracted

          if (exMotif) setMotif(matchMotif(exMotif))
          if (exGravite) {
            const upper = exGravite.toUpperCase()
            if (['LEGERE', 'MODEREE', 'GRAVE', 'CRITIQUE'].includes(upper)) {
              setGravite(upper)
            }
          }
          if (exBesoins?.length) setBesoins(matchBesoins(exBesoins))
          if (exContexte) setContexte(exContexte)

          toast.success('Transcription terminee — verifiez les champs')
        } else {
          // Extraction failed but transcription succeeded — put in contexte
          setContexte(result.transcription)
          toast('Transcription recue (extraction automatique indisponible)', { icon: 'i' })
        }
      } catch (err: any) {
        toast.error(err?.message || 'Erreur de transcription')
      } finally {
        recorder.setState('idle')
      }
    } else {
      // Start recording
      await recorder.start()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!motif || !gravite) return
    onSubmit({ motif, gravite: gravite as OrientationRequest['gravite'], besoins, contexte, latitude, longitude })
  }

  const isProcessing = recorder.state === 'processing'
  const isRecording = recorder.state === 'recording'

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Voice Transcription */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-semibold text-purple-900">Dictee vocale</h3>
            <p className="text-xs text-purple-600">Decrivez le cas clinique a voix haute — les champs seront pre-remplis automatiquement</p>
          </div>
          <button
            type="button"
            onClick={handleVoiceToggle}
            disabled={isProcessing || isLoading}
            className={`relative flex items-center justify-center w-14 h-14 rounded-full transition-all shadow-lg ${
              isRecording
                ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                : isProcessing
                  ? 'bg-purple-400 cursor-wait'
                  : 'bg-purple-600 hover:bg-purple-700'
            } text-white disabled:opacity-50`}
          >
            {isRecording ? (
              <StopIcon className="w-6 h-6" />
            ) : isProcessing ? (
              <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <MicrophoneIcon className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Recording indicator */}
        {isRecording && (
          <div className="flex items-center gap-2 mt-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm text-red-700 font-medium">
              Enregistrement en cours... {recorder.duration}s
            </span>
            <button
              type="button"
              onClick={recorder.cancel}
              className="ml-auto text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
            >
              <XMarkIcon className="w-3.5 h-3.5" /> Annuler
            </button>
          </div>
        )}

        {isProcessing && (
          <p className="text-sm text-purple-700 mt-2">Analyse en cours...</p>
        )}

        {recorder.error && (
          <p className="text-sm text-red-600 mt-2">{recorder.error}</p>
        )}

        {/* Show raw transcription */}
        {transcription && !isRecording && !isProcessing && (
          <div className="mt-3 bg-white/70 rounded-lg p-3 text-sm text-neutral-700 border border-purple-100">
            <span className="text-xs font-medium text-purple-500 block mb-1">Transcription :</span>
            {transcription}
          </div>
        )}
      </div>

      {/* Motif */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">Motif d'orientation *</label>
        <select
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
        >
          <option value="">Selectionnez un motif...</option>
          {MOTIFS.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {/* Gravite */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">Gravite *</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {GRAVITES.map((g) => (
            <button
              key={g.value}
              type="button"
              onClick={() => setGravite(g.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                gravite === g.value
                  ? `${g.color} border-current`
                  : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Besoins */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">Besoins specifiques</label>
        <div className="flex flex-wrap gap-2">
          {BESOINS.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => toggleBesoin(b)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                besoins.includes(b)
                  ? 'bg-primary-100 text-primary-800 border-primary-300'
                  : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
              }`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      {/* Contexte */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">Contexte supplementaire</label>
        <textarea
          value={contexte}
          onChange={(e) => setContexte(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Informations supplementaires..."
        />
      </div>

      {/* Geolocation */}
      <div className="bg-neutral-50 rounded-lg p-3 text-xs text-neutral-500">
        <span className="inline-block w-2 h-2 rounded-full bg-success-500 mr-1.5" />
        {geoStatus || 'En attente de localisation...'}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={!motif || !gravite || isLoading}
        className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? 'Calcul en cours...' : 'Lancer l\'orientation'}
      </button>
    </form>
  )
}
